// Multipart upload client for large (50GB+) files.
//
// It talks to the presigned-URL backend (backend/api/uploadServer.js):
//   POST {API}/uploads/create   {key, contentType}                -> {uploadId}
//   POST {API}/uploads/sign     {key, uploadId, partNumber}       -> {url}
//   POST {API}/uploads/complete {key, uploadId, parts[]}          -> {location}
//   POST {API}/uploads/abort    {key, uploadId}
//
// The browser PUTs each part straight to S3 (no proxying through the backend),
// so it scales to very large files. Set REACT_APP_UPLOAD_API to enable it.
import firebase from '../firebaseConfig';
import { runMultipart, PART_SIZE, putPart } from '../utils/multipart';
import { deviceFingerprint } from '../utils/fingerprint';

import { API_BASE } from './env';

const API = API_BASE;
const PUBLIC_CDN = (process.env.REACT_APP_PUBLIC_CDN || '').replace(/\/$/, '');
const SESSION_PREFIX = 'kit_upload_session:';

export const isUploadConfigured = () => !!API;

const safeGet = (k) => {
  try {
    const raw = localStorage.getItem(k);
    return raw ? JSON.parse(raw) : null;
  } catch (_e) {
    return null;
  }
};
const uploadSession = (id) => ({
  load: () => safeGet(`${SESSION_PREFIX}${id}`),
  save: (s) => {
    try {
      localStorage.setItem(`${SESSION_PREFIX}${id}`, JSON.stringify(s));
    } catch (_e) {
      /* ignore */
    }
  },
  clear: () => {
    try {
      localStorage.removeItem(`${SESSION_PREFIX}${id}`);
    } catch (_e) {
      /* ignore */
    }
  },
});

// Public CDN URL for an object stored in the public bucket.
export const cdnUrl = (key) => (PUBLIC_CDN ? `${PUBLIC_CDN}/${key}` : '');

async function authHeaders() {
  const user = firebase.auth().currentUser;
  const token = user ? await user.getIdToken() : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function api(path, body, signal) {
  const res = await fetch(`${API}${path}`, {
    method: 'POST',
    signal,
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail = '';
    try {
      const body = await res.json();
      detail = body?.error ? ` — ${body.error}` : '';
    } catch {
      /* non-JSON body */
    }
    const err = new Error(`${path} failed: ${res.status}${detail}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

// Uploads one file via S3 multipart, resilient to outages and resumable from
// the last finished part. onProgress(fraction 0..1); optional onState reports
// reconnect attempts; optional monitor receives live telemetry.
// bucket: 'raw' (default, originals) or 'public' (CDN-served derived files).
export async function uploadFile(
  file,
  { key, contentType, onProgress, onState, onRecognized, monitor, signal, bucket = 'raw' }
) {
  if (!isUploadConfigured()) {
    throw new Error(
      'Upload backend not configured. Set REACT_APP_UPLOAD_API once the ' +
        'presigned-URL service is deployed.'
    );
  }
  const resumeId = `${bucket}|${file.name}|${file.size}|${file.lastModified || 0}`;
  const { key: usedKey, result } = await runMultipart(file, {
    create: () => api('/uploads/create', { key, contentType: contentType || file.type, bucket }),
    sign: (uploadId, k, partNumber) => api('/uploads/sign', { key: k, uploadId, partNumber, bucket }),
    complete: (uploadId, k, parts) => api('/uploads/complete', { key: k, uploadId, parts, bucket }),
    listParts: (uploadId, k) => api('/uploads/parts', { key: k, uploadId, bucket }),
    session: uploadSession(resumeId),
    onProgress,
    onState,
    onRecognized,
    monitor,
    signal,
    fileId: resumeId,
    partSize: PART_SIZE,
  });
  // `usedKey` is authoritative (a resumed upload keeps its original key).
  return { ...(result || {}), key: usedKey };
}

// One shot for a short recording. The big-file uploader retries a dead
// connection forever, which leaves a face clip sitting on "Saving…".
export async function uploadOnce(file, { key, contentType, timeoutMs = 45000 } = {}) {
  if (!isUploadConfigured()) throw new Error('Upload backend not configured.');
  const type = String(contentType || file.type || 'application/octet-stream').split(';')[0];
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const created = await api('/uploads/create', { key, contentType: type, bucket: 'raw' }, ctrl.signal);
    const signed = await api('/uploads/sign', { key: created.key, uploadId: created.uploadId, partNumber: 1, bucket: 'raw' }, ctrl.signal);
    const etag = await putPart(signed.url, file, () => {}, ctrl.signal, 15000);
    if (!etag) throw new Error('the file store did not accept the recording');
    await api('/uploads/complete', {
      key: created.key,
      uploadId: created.uploadId,
      parts: [{ ETag: etag, PartNumber: 1 }],
      bucket: 'raw',
    }, ctrl.signal);
    return { key: created.key };
  } catch (err) {
    if (err.name === 'AbortError') throw new Error('Saving took too long. Record that clip again.');
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

// Abort an in-flight authed multipart upload (best effort) + clear checkpoint.
export async function abortUpload(file, { bucket = 'raw' } = {}) {
  const resumeId = `${bucket}|${file.name}|${file.size}|${file.lastModified || 0}`;
  const sess = uploadSession(resumeId);
  const saved = sess.load();
  sess.clear();
  if (saved?.uploadId) {
    await api('/uploads/abort', { key: saved.key, uploadId: saved.uploadId, bucket }).catch(() => {});
  }
}

// Server-observed uploader provenance (IP + geo-IP) for the signed-in user.
export async function getUploaderContext() {
  if (!API) return null;
  try {
    const res = await fetch(`${API}/uploader-context`, { headers: { ...(await authHeaders()) } });
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

// Full uploader metadata for an in-app upload: server-verified IP/geo-IP merged
// with this browser's device fingerprint. NO permission-gated collection — the
// geo here is derived server-side from the IP, never the browser GPS prompt.
export async function collectUploaderMeta() {
  const fingerprint = deviceFingerprint();
  const server = await getUploaderContext();
  return {
    ...(server || {}),
    capturedAt: Date.now(),
    client: {
      fingerprintId: fingerprint.id,
      userAgent: fingerprint.userAgent,
      platform: fingerprint.platform,
      vendor: fingerprint.vendor,
      languages: fingerprint.languages,
      timezone: fingerprint.timezone,
      screen: fingerprint.screen,
      cores: fingerprint.cores,
      deviceMemory: fingerprint.deviceMemory,
      touch: fingerprint.touch,
    },
    macAddress: null, // not obtainable from a browser
  };
}

export function buildKey(prefix, fileName) {
  const safe = fileName.replace(/[^\w.\-]+/g, '_');
  return `${prefix}${Date.now()}-${safe}`;
}

// Presigned GET URL for streaming a raw object from the private bucket.
export async function getAssetPlaybackUrl(key) {
  if (!isUploadConfigured()) {
    throw new Error('Playback backend not configured (REACT_APP_UPLOAD_API).');
  }
  const { url } = await api('/assets/playback', { key });
  return url;
}

// Presigned GET that downloads an asset with a friendly filename.
export async function getAssetDownloadUrl({ key, name, bucket = 'raw' }) {
  if (!isUploadConfigured()) throw new Error('Download backend not configured.');
  const { url } = await api('/assets/download', { key, name, bucket });
  return url;
}

// Off-site backup status for a raw asset: which parts are mirrored to the
// Glacier backup, their storage class, and the live object's replication state.
// `parts` is the asset's {s3Key,name}[]; falls back to a single {key}.
export async function getAssetBackupStatus({ key, parts }) {
  if (!isUploadConfigured()) throw new Error('Backend not configured.');
  return api('/assets/backup-status', { key, parts });
}

// Trigger a browser download for an asset stored in S3.
export async function downloadAsset({ key, name, bucket = 'raw' }) {
  const url = await getAssetDownloadUrl({ key, name, bucket });
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noreferrer';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  return url;
}

// Permanently delete a single S3 object.
export async function deleteRemoteObject({ key, bucket = 'raw' }) {
  if (!isUploadConfigured()) throw new Error('Backend not configured.');
  return api('/assets/delete', { key, bucket });
}

// Permanently delete every object under a prefix (a whole item folder).
export async function deleteRemotePrefix({ prefix, bucket = 'raw' }) {
  if (!isUploadConfigured()) throw new Error('Backend not configured.');
  return api('/assets/delete-prefix', { prefix, bucket });
}
